import React, { useCallback } from 'react'
import { Divider, DropdownOnSearchChangeData } from 'semantic-ui-react'
import { useField } from 'react-final-form'
import debounce from 'debounce'

import {
  Form,
  Input,
  Masked,
  Phone,
  Select,
  Checkbox,
  DateInput
} from '../../../../../../../components/Form'

import { CustomRadio } from '../../../../../../../components/Form/CustomRadio'
import { Show } from '../../../../../../../components/Show'
import { SelectOption } from '../../../../../../../components/Form/Select'

import {
  partnerToOption,
  responsibleToOption
} from '../../../../../utils/selectOptions'

import { useResponsible, usePartner, usePerson } from '../../../hooks'
import { CnpjInput } from './CnpjInput'

const DEBOUNCE_TIME = 500

export const PersonFields: React.FC = () => {
  const { responsible, partner, setLoading } = usePerson()
  const { people, loading: loadingResponsibles, listPeople } = useResponsible()
  const { partners, loading: loadingPartners, listPartners } = usePartner()

  const typeField = useField('type')
  const activeField = useField('active')

  const partnerIdFieldForDebug = useField('partnerId', {
    subscription: { value: true }
  })
  const responsibleIdFieldForDebug = useField('responsibleId', {
    subscription: { value: true }
  })

  const buildOptions = (
    initialData: { id: string; [key: string]: any } | undefined,
    initialDataMapper: (data: any) => SelectOption,
    listData: any[],
    listDataMapper: (data: any) => SelectOption,
    formValueId: string | undefined
  ): SelectOption[] => {
    const options = listData.map(listDataMapper)

    if (initialData && !options.some(opt => opt.value === initialData.id)) {
      options.unshift(initialDataMapper(initialData))
    }

    if (
      formValueId &&
      !initialData &&
      !options.some(opt => opt.value === formValueId)
    ) {
      options.unshift({
        key: formValueId,
        value: formValueId,
        text: 'Carregando...'
      })
    }

    return options
  }

  const finalResponsibleOptions = buildOptions(
    responsible,
    responsibleToOption,
    people,
    responsibleToOption,
    responsibleIdFieldForDebug.input.value as string
  )

  const finalPartnerOptions = buildOptions(
    partner,
    partnerToOption,
    partners,
    partnerToOption,
    partnerIdFieldForDebug.input.value as string
  )

  const searchResponsible = useCallback(
    debounce((searchQuery: string) => {
      listPeople(searchQuery)
    }, DEBOUNCE_TIME),
    [listPeople]
  )

  const searchPartner = useCallback(
    debounce((searchQuery: string) => {
      listPartners(searchQuery)
    }, DEBOUNCE_TIME),
    [listPartners]
  )

  const handleResponsibleSearch = (data: DropdownOnSearchChangeData) => {
    searchResponsible(data.searchQuery)
  }

  const handlePartnerSearch = (data: DropdownOnSearchChangeData) => {
    searchPartner(data.searchQuery)
  }

  const isCompany = typeField.input.value === 'COMPANY'
  const isIndividual = typeField.input.value === 'INDIVIDUAL'

  return (
    <>
      <Divider horizontal content="Dados" />
      <input name="id" type="hidden" />

      <Form.Group inline>
        <CustomRadio name="type" label="Pessoa Jurídica" value="COMPANY" />
        <CustomRadio name="type" label="Pessoa Física" value="INDIVIDUAL" />
      </Form.Group>

      <Form.Group>
        <Show condition={isCompany}>
          <CnpjInput
            name="documentFormatted"
            mask="##.###.###/####-##"
            label="CNPJ"
            width={4}
            onLoading={setLoading}
          />
        </Show>

        <Show condition={isIndividual}>
          <Masked
            name="documentFormatted"
            mask="###.###.###-##"
            label="CPF"
            width={4}
          />
        </Show>

        <Input
          name="name"
          label="Nome"
          width={8}
          maxLength={100}
          required
          uppercase
        />

        <DateInput
          name="birthDateFormatted"
          width={4}
          label={isCompany ? 'Data de fundação' : 'Data de nascimento'}
        />
      </Form.Group>

      <Show condition={isCompany}>
        <Input
          name="tradeName"
          label="Nome Comercial"
          maxLength={100}
          uppercase
        />

        <Select
          name="responsibleId"
          label="Responsável"
          options={finalResponsibleOptions}
          loading={loadingResponsibles}
          onSearchChange={(_, data) => handleResponsibleSearch(data)}
          search
          uppercase
        />
      </Show>

      <Form.Group>
        <Input
          name="emailsFormatted"
          label="E-mail"
          required
          width={12}
          lowercase
        />

        <Phone name="phoneFormatted" label="Telefone" required width={4} />
      </Form.Group>

      <Form.Group>
        <Select
          name="partnerId"
          label="Parceiro"
          options={finalPartnerOptions}
          loading={loadingPartners}
          onSearchChange={(_, data) => handlePartnerSearch(data)}
          width={12}
          search
          uppercase
          required
        />

        <Checkbox
          name="active"
          label="Ativo"
          checked={!!activeField.input.value}
          width={4}
          alignField
        />
      </Form.Group>
    </>
  )
}
